/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import router from 'next/router';
import {
  TestComponentStates,
  TestComponentStatesData,
  TestProgress,
  testCheckPoints,
} from './@types';
import Button from '../Button';
import { ROUTES } from '../../Constants/variables';
import SvgLib from '../SvgLib';
import Typo from '../Typo';
import apiStatusCheck from '../../models/HealthCheck/api';
import { logger } from '../../utils';
import useAsyncTask from '../../hooks/useAsyncTask';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import styles from './testComponent.module.sass';

const YOUTUBE_IMG_URL = 'https://www.youtube.com/img/desktop/yt_1200.png';
const H5P_IMG_URL = 'https://h5p.org/sites/all/themes/professional_themec/favicon.ico';

const StatusIcon = ({ status }: { status: TestProgress }) => {
  if (status === 'loading') {
    return (
      <Button
        isLoader
        classes="is-loading"
        style={{
          marginRight: '20px',
        }}
      />
    );
  }
  if (status === 'success') {
    return <SvgLib icon="status-ok" />;
  }
  return <SvgLib icon="status-error" />;
};

const TestComponent = () => {
  const { isMobile } = useIsMobileViewport();

  const [testState, setTestState] = useState<TestComponentStates>('startTest');
  const [testStateData, setTestStateData] = useState(TestComponentStatesData[testState]);

  const [apiResponsive, setApiResponsive] = useState<TestProgress>('default');
  const [apiReachable, setApiReachable] = useState<TestProgress>('default');
  const [youtubeAccess, setYoutubeAccess] = useState<TestProgress>('default');
  const [h5pAccess, setH5PAccess] = useState<TestProgress>('default');

  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);

  useEffect(() => {
    if (successCount === testCheckPoints.length) {
      setTestState('testPassed');
      setTestStateData(TestComponentStatesData.testPassed);
    } else if (failCount + successCount === testCheckPoints.length) {
      setTestState('testFailed');
      setTestStateData(TestComponentStatesData.testFailed);
    }
  }, [successCount, failCount]);

  const onFAQClick = async () => {
    router.push(ROUTES.FAQ);
  };
  const onHomeClick = async () => {
    router.push(ROUTES.HOME);
  };

  const checkApiStatus = async (
    setStatus: Dispatch<SetStateAction<TestProgress>>,
    url: any,
    type: 'internalAPIResponsive' | 'internalAPIReachable' | 'youtube' | 'h5p',
  ) => {
    try {
      setStatus('loading');
      if (type === 'h5p' || type === 'youtube') {
        const imageDoc = document.createElement('img');
        imageDoc.src = url;
        imageDoc.addEventListener('load', () => {
          const isLoaded = imageDoc.complete && imageDoc.naturalHeight !== 0;
          if (isLoaded) {
            setStatus('success');
            setSuccessCount(successCount => successCount + 1);
          } else {
            setStatus('failed');
            setFailCount(failCount => failCount + 1);
          }
        });
        imageDoc.addEventListener('error', () => {
          setStatus('failed');
          setFailCount(failCount => failCount + 1);
        });

        imageDoc.style.display = 'none';
        document.body.appendChild(imageDoc);
      } else {
        const { data, code } = await url;
        if (
          (type === 'internalAPIResponsive' && data === 'Server is healthy.') ||
          (type === 'internalAPIReachable' && code === 200)
        ) {
          setStatus('success');
          setSuccessCount(successCount => successCount + 1);
        }
      }
    } catch (error) {
      logger.error(error);
      setStatus('failed');
      setFailCount(failCount => failCount + 1);
    }
  };

  const handleTestCases = useAsyncTask(async () => {
    checkApiStatus(setApiReachable, apiStatusCheck(), 'internalAPIReachable');
    checkApiStatus(setApiResponsive, apiStatusCheck(), 'internalAPIResponsive');
    checkApiStatus(setYoutubeAccess, YOUTUBE_IMG_URL, 'youtube');
    checkApiStatus(setH5PAccess, H5P_IMG_URL, 'h5p');
  });

  return (
    <Formik
      onSubmit={(val, helpers) => {
        testState === 'startTest' && handleTestCases.run({ values: val, formikHelpers: helpers });

        testState === 'startTest'
          ? (setTestStateData(TestComponentStatesData.inProgress),
            setSuccessCount(() => 0),
            setFailCount(() => 0),
            setTestState('inProgress'))
          : testState === 'inProgress'
            ? (setTestStateData(TestComponentStatesData.startTest),
              setSuccessCount(() => 0),
              setFailCount(() => 0),
              setTestState('startTest'))
            : testState === 'testFailed'
              ? onFAQClick()
              : onHomeClick();
      }}
      initialValues={{}}
      enableReinitialize
    >
      {(formikProps: FormikProps<Partial<{ id: string }>>) => {
        return (
          <form onSubmit={formikProps.handleSubmit} id="test_knopro_works">
            <div
              className={clsx(styles.root, {
                'is-flex is-flex is-justify-content-space-evenly has-text-left':
                  testState === 'testPassed' || testState === 'testFailed',
                'is-flex-direction-column':
                  isMobile && (testState === 'testPassed' || testState === 'testFailed'),
              })}
              key={Math.random()}
            >
              <div>
                {testState === 'testPassed' && (
                  <SvgLib icon="big-smiley" iconSize="fullWidth" classes={styles.icon} />
                )}
                {testState === 'testFailed' && (
                  <SvgLib icon="big-cross" iconSize="fullWidth" classes={styles.icon} />
                )}
              </div>

              <div className={clsx({ 'has-text-centered': isMobile })}>
                <Typo h2>{testStateData.title}</Typo>

                <div className="my-4">
                  {Array.isArray(testStateData.description) ? (
                    testStateData.description.map(data => (
                      <div
                        className={clsx('is-flex  has-text-centered my-3', {
                          'has-text-left': isMobile,
                          'is-justify-content-center':
                            testState === 'inProgress' || testState === 'startTest',
                        })}
                      >
                        {data.type === 'api-reachable' && <StatusIcon status={apiReachable} />}
                        {data.type === 'api-responsive' && <StatusIcon status={apiResponsive} />}
                        {data.type === 'access-youtube' && <StatusIcon status={youtubeAccess} />}
                        {data.type === 'access-h5p' && <StatusIcon status={h5pAccess} />}
                        <Typo>{data.text}</Typo>
                      </div>
                    ))
                  ) : (
                    <Typo>{testStateData.description}</Typo>
                  )}
                </div>
                <Button
                  theme="Filled - Lime"
                  disabled={handleTestCases.status === 'PROCESSING'}
                  type="submit"
                  classes={`${handleTestCases.status === 'PROCESSING' && 'is-loading'}`}
                >
                  {testStateData.buttonText}
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default TestComponent;
